import type {
  ActionQueue,
  ActionQueueRequest,
  ActionQueueResult,
  ActionQueueStats,
  AppUpdater,
  KioskState,
  PendingAction,
  PlatformBridge,
  OpenFileDialogOptions,
  SecureStore,
  UpdateStatusSnapshot,
} from './PlatformBridge.js';

/**
 * Web fallback for PlatformBridge. Used when the React app runs in an
 * ordinary browser (i.e. the existing web build). Step 3 of the developer
 * instructions: "Provide a WEB implementation (browser fallbacks: normal
 * download, browser print dialog, cookie/localStorage token)."
 *
 * The desktop build replaces these with privileged main-process calls.
 */

const localSecureStore: SecureStore = {
  async get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  async set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* quota / privacy mode — silently ignore */
    }
  },
  async delete(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

function downloadBytes(name: string, bytes: Uint8Array): void {
  // Coerce to a fresh ArrayBuffer so the Blob constructor accepts it across
  // every TS lib target without complaining about ArrayBufferLike unions.
  const copy = new Uint8Array(bytes);
  const blob = new Blob([copy.buffer], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the click has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function pickFiles(opts: OpenFileDialogOptions = {}): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = !!opts.multi;
    if (opts.filters && opts.filters.length) {
      const accept = opts.filters
        .flatMap((f) => f.extensions.map((ext) => `.${ext.replace(/^\./, '')}`))
        .join(',');
      if (accept) input.accept = accept;
    }
    input.onchange = () => {
      const files = input.files ? Array.from(input.files) : [];
      resolve(files);
    };
    // Some browsers need the input attached for `.click()` on certain code
    // paths; do it for safety.
    input.style.display = 'none';
    document.body.appendChild(input);
    input.click();
    // Clean up shortly after — the change event fires synchronously off click.
    setTimeout(() => {
      if (input.parentNode) input.parentNode.removeChild(input);
    }, 0);
  });
}

export interface WebPlatformBridgeOptions {
  /** Build-time version string (e.g. injected from package.json). */
  appVersion?: string;
}

/**
 * Pass-through "queue" for the web build. The doc (Step 13) explicitly
 * scopes the offline queue to the desktop build — the browser has no
 * usable durable store for cross-tab background retry, and the existing
 * web ERP must continue to behave exactly as before. So on web we just
 * issue the fetch synchronously; failures bubble back to the caller, who
 * is expected to gate offline-aware UX on `platformBridge.isDesktop`.
 */
function createWebActionQueue(): ActionQueue {
  const noopUnsubscribe = (): void => {
    /* no broadcast on web */
  };

  return {
    async enqueue(req: ActionQueueRequest): Promise<ActionQueueResult> {
      const headers = new Headers(req.headers ?? {});
      if (req.body !== undefined && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      const res = await fetch(req.url, {
        method: req.method,
        headers,
        body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`Request failed (${res.status})`);
      }
      return { queueId: '', status: 'delivered' };
    },
    async list(): Promise<PendingAction[]> {
      return [];
    },
    async stats(): Promise<ActionQueueStats> {
      return { pending: 0, awaitingReauth: 0, failed: 0, flushing: false };
    },
    async flush(): Promise<void> {
      /* web writes go out synchronously — there is never anything to drain */
    },
    async retry(): Promise<void> {
      /* nothing to retry */
    },
    async discard(): Promise<void> {
      /* nothing to discard */
    },
    onChange(): () => void {
      return noopUnsubscribe;
    },
  };
}

export function createWebPlatformBridge(opts: WebPlatformBridgeOptions = {}): PlatformBridge {
  const version = opts.appVersion ?? '0.0.0';

  const idleStatus: UpdateStatusSnapshot = { phase: 'idle' };
  const noopUpdater: AppUpdater = {
    enabled: false,
    async getStatus() {
      return idleStatus;
    },
    onStatus() {
      return () => {
        /* no-op */
      };
    },
    async quitAndInstall() {
      /* no-op on web */
    },
    async checkForUpdates() {
      /* no-op on web */
    },
  };

  const noopKiosk: KioskState = { enabled: false };

  return {
    isDesktop: false,
    appVersion: () => version,
    onUpdateAvailable: () => {
      /* no-op on web */
    },
    secureStore: localSecureStore,
    actionQueue: createWebActionQueue(),
    updates: noopUpdater,
    kiosk: noopKiosk,
    async printSilent(pdf) {
      // Browsers cannot print silently — show the OS print dialog with the
      // PDF rendered in a hidden iframe.
      const copy = new Uint8Array(pdf);
      const blob = new Blob([copy.buffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      document.body.appendChild(iframe);
      await new Promise<void>((resolve) => {
        iframe.onload = () => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          } finally {
            resolve();
          }
        };
      });
      // Defer cleanup so the print dialog can read from the iframe.
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 60_000);
    },
    async saveFile(name, bytes) {
      downloadBytes(name, bytes);
      return '';
    },
    openFileDialog(opts) {
      return pickFiles(opts);
    },
  };
}
